import {
  AfterViewInit,
  Component,
  effect,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  QueryList,
  Signal,
  ViewChild,
  ViewChildren
} from '@angular/core';
import {StockService} from '../../services/stock/stock.service';
import {StockInputMode, StockOperation} from '../../models/enums';
import {UiToastService} from '../../shared/ui';
import {faBarcode} from '@fortawesome/free-solid-svg-icons';
import {ScanHistoryItem} from '../../models/scan-history';
import {Subscription} from 'rxjs';
import {Context} from '../../models/context';
import {ContextService} from '../../services/context/context-signal.service';
import {Household} from '../../models/household';
import {UiModalComponent} from '../../shared/ui';
import { AppLogger, AppLoggerService } from '../../core/logging';

@Component({
  selector: 'app-quick-controls',
  templateUrl: './quick-controls.component.html',
  styleUrls: ['./quick-controls.component.scss'],
  standalone: false
})
export class QuickControlsComponent implements AfterViewInit, OnInit, OnChanges {

  private readonly tag = '[QuickControls]';
  private readonly log: AppLogger;

  isMobile = false;

  context: Signal<Context> = this.contextService.context;

  @Input() storeId: number;
  @Output() operationCompleted = new EventEmitter<ScanHistoryItem[]>();

  @ViewChildren('code', {read: ElementRef}) inputCodeElement: QueryList<ElementRef>;
  @ViewChild('scannerModal') scannerModal?: UiModalComponent;
  @ViewChild('scanner') scanner?: any;

  // Icons
  faScan = faBarcode;

  // Product (Input)
  input: string;
  expiryDate: Date | null = null;

  // Product Context
  operation: StockOperation;

  // State
  tmpHistory: ScanHistoryItem[] = [];

  inputMode: StockInputMode;
  eStockInputMode = StockInputMode;
  eStockOperation = StockOperation;

  // Scanner state
  scannerReady = false;

  private focusSubscriptionInitialized = false;
  private $elementSubscription?: Subscription;
  protected household: Household = null;

  constructor(
    private contextService: ContextService,
    private appLogger: AppLoggerService,
    private stockService: StockService,
    public toastService: UiToastService,
  ) {
    this.log = this.appLogger.createForClass(QuickControlsComponent);

    this.inputMode = StockInputMode.Form;
    this.operation = StockOperation.Add;

    effect(() => {
      const c = this.context();
      if (!c?.isSet?.()) return;

      this.household = c.household;
    });
  }

  static resolveOperation(operation: number): string {
    switch (operation) {
      case StockOperation.Add:
        return 'Add';
      case StockOperation.Remove:
        return 'Remove';
      default:
        return 'Invalid Operation';
    }
  }

  ngOnInit(): void {
    this.log.info('Initialized stock quick controls');
    this.isMobile = window.innerWidth < 768;
  }

  ngAfterViewInit(): void {
    this.log.debug('After view init', { inputCount: this.inputCodeElement?.length ?? 0 });
    this.setFocusIfReady();
  }

  ngOnChanges(): void {
    this.log.debug('Store changed', { storeId: this.storeId });
    this.setFocusIfReady();
  }

  setFocusIfReady(): void {
    if (this.storeId === null) {
      this.focusSubscriptionInitialized = false;

      if (this.$elementSubscription) {
        this.$elementSubscription.unsubscribe();
      }
      return;
    }

    if (this.inputCodeElement?.first) {
      this.setFocus(this.inputCodeElement);
    }

    if (!this.focusSubscriptionInitialized && this.inputCodeElement) {
      this.focusSubscriptionInitialized = true;
      this.$elementSubscription = this.inputCodeElement.changes.subscribe(() => {
        this.setFocus(this.inputCodeElement);
      });
    }
  }

  setFocus(ql: QueryList<ElementRef>): void {
    if (this.inputMode !== StockInputMode.Scan) {
      setTimeout(() => {
        ql.first.nativeElement.focus();
      }, 50);
    }
  }

  onSubmit(): void {
    this.log.debug('Submit', { input: this.input, storeId: this.storeId, operation: this.operation });

    if (this.input == null) {
      return;
    }

    this.log.info('Stock operation requested', {
      operation: QuickControlsComponent.resolveOperation(this.operation),
      barcode: this.input,
      storeId: this.storeId
    });

    switch (this.operation) {
      case StockOperation.Add: {
        const expDate = this.expiryDate ? new Date(this.expiryDate) : undefined;

        this.stockService.addProductToStockByCode(this.input, this.storeId, expDate).subscribe(
          (resp) => {
            const firstItem = resp[0];

            this.toastService.success(`Added ${firstItem.product.name} (${firstItem.id}) to ${this.storeId}`, { durationMs: 2000 });
            this.onOperationFinished({operation: 'ADD', id: firstItem.id, name: firstItem.product.name});
            this.input = '';
          },
          error => {
          }
        );
        break;
      }
      case StockOperation.Remove:
        this.stockService.removeProductFromStock(this.input, this.storeId, 1).subscribe(
          (resp) => {
            this.log.info('Removed product from stock', { response: resp, barcode: this.input, storeId: this.storeId });
            this.toastService.success(`Removed ${this.input} from ${this.storeId}`, { durationMs: 2000 });
            this.onOperationFinished({operation: 'REMOVE', code: this.input});
            this.input = '';
          }
        );
        break;
    }

    this.setFocus(this.inputCodeElement);
  }

  changeInputMode(mode: StockInputMode): void {
    this.inputMode = mode;
  }

  setOperation(operation: StockOperation): void {
    if (this.operation === operation) {
      return;
    }

    this.operation = operation;
    this.setFocus(this.inputCodeElement);
  }

  onDateSelected(): void {
    this.setFocus(this.inputCodeElement);
  }

  onScannerReadyChanged(readyness: boolean): void {
    this.scannerReady = readyness;
  }

  onProductScanned(code: string): void {
    this.input = code;
    this.toastService.success(code);
  }

  onOperationFinished(historyItem: ScanHistoryItem): void {
    this.tmpHistory.unshift(historyItem);
    this.operationCompleted.emit(this.tmpHistory);
  }

  perform(): void {
    this.onSubmit();
  }

  protected readonly StockOperation = StockOperation;

  openScanner(): void {
    this.scannerModal?.open();

    // Defer start until the modal is opened and view is stable.
    setTimeout(() => {
      this.scanner?.start?.();
    }, 0);
  }

  onScannerClosed(): void {
    this.scanner?.stop?.();
    // When modal closes, restore focus to the code input.
    this.setFocusIfReady();
  }

  onScannerCodeAccepted(code: string): void {
    if (!code) return;

    this.input = code;
    this.scanner?.stop?.();
    this.scannerModal?.close();
    this.setFocusIfReady();
  }
}
